import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BrandConfigQuery } from '@app/store/brand/brand.query';
import { getCdnAsset } from '@constants/api-endpoints';
import { IPartnerData } from '@models/WhitelabelConfig';
import { formatPhoneNumber } from '@utils/phone-number-formatter';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
	selector: 'qe-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
	/** Set as defaults, but this will allow us to change values based on brand/whitelabel */
	public whitelabelLogoSrc$: Observable<string>;
	public contactPhoneNumber: Observable<string | undefined>;
	public dialablePhoneNumber: string | undefined;
	public formattedPhoneNumber!: string | undefined;
	public logoSrc: string;
	public brandLockup: boolean = false;
	public embraceLogoFirst: boolean = false;
	public isClickablePartnerLogo: boolean = false;
	public isClickableEmbraceLogo: boolean = false;
	public whitelabelLogoTarget: string | undefined;
	public isAllstate: boolean = false;
	public readonly brandLogo: string = 'assets/logos/embrace-pet-insurance.png';
	public readonly brandLogoWhite: string = 'assets/logos/embrace-pet-insurance.png';
	public readonly brandLogoDark: string = 'assets/logos/embrace-pet-insurance-dark.png';
	public readonly brandConfig$: Observable<IPartnerData>;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
	private readonly mobileBreakpoint: string = '(max-width: 768px)';

	constructor(
		private readonly breakpoint: BreakpointObserver,
		private readonly brandConfigGetters: BrandConfigQuery,
	) {
		this.brandConfig$ = this.brandConfigGetters.brandConfig$();
		this.whitelabelLogoSrc$ = this.brandConfig$
			.pipe(map(config => `${getCdnAsset(config?.whiteLabelConfig?.quote?.partnerAssets?.logoLocation)}`),
			);
		this.logoSrc = this.brandLogo;
		this.contactPhoneNumber = this.brandConfigGetters.phone$();
		this.contactPhoneNumber
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(phoneNumber => {
				this.formattedPhoneNumber = formatPhoneNumber(phoneNumber);
			});
	}

	ngOnInit(): void {
		combineLatest([
			this.breakpoint.observe([this.mobileBreakpoint]),
			this.brandConfig$,
		]).pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(([_, brand]) => {
				this.brandLockup = brand?.whiteLabelConfig?.quote?.brandLockup;
				this.embraceLogoFirst = !!brand?.whiteLabelConfig?.quote?.embraceLogoFirst;
				this.whitelabelLogoTarget = brand?.whiteLabelConfig?.quote?.logoUrlTarget;

				this.isClickablePartnerLogo =
					this.brandLockup &&
					!!brand.whiteLabelConfig.quote?.partnerAssets?.logoLocation &&
					!!brand?.whiteLabelConfig?.quote?.logoUrlTarget;

				this.isClickableEmbraceLogo =
					brand?.id === 'embrace' &&
					!!brand?.whiteLabelConfig?.quote?.logoUrlTarget;

				// Allstate requested specific header changes that do not apply to any other brand
				this.isAllstate = brand?.id?.toLowerCase() === 'allstate';
				const partnerPhoneNumber: string = brand?.partnerPhoneNumber ?? ``;
				const strippedPartnerPhoneNumber: string = partnerPhoneNumber.replace(/\D/g, ``);
				this.dialablePhoneNumber = strippedPartnerPhoneNumber;

				if (brand?.id?.toLowerCase() === 'goodsam') {
					this.logoSrc = this.brandLogoDark;
				} else if (brand?.id?.toLowerCase() !== 'embrace'
					&& this.breakpoint.isMatched(this.mobileBreakpoint)) {
					this.logoSrc = this.brandLogoWhite;
				} else {
					this.logoSrc = this.brandLogo;
				}
			});
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
